import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { GeneralService } from '../../../core/services/general.service';
import { debounceTime, distinctUntilChanged, Subject, switchMap, of } from 'rxjs';

const END_POINT = `${environment.SERVICES_URL}`;
const API_KEY = `${environment.API_KEY}`;
const NOMINATIM = 'https://nominatim.openstreetmap.org/search';
const USER_AGENT = 'SafeAndConfidenceApp/1.0';

@Component({
    selector: 'app-location-picker',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './location-picker.component.html',
    styleUrl: './location-picker.component.scss'
})
export class LocationPickerComponent implements OnInit, OnChanges {
    /** FormGroup del grupo de dirección (serviceForm.get('origin') o 'destination') */
    @Input() group!: FormGroup;
    /** users.id del cliente logueado (null si es admin) */
    @Input() clientId: number | null = null;
    /** Lista de ciudades disponibles para mapeo */
    @Input() cityList: any[] = [];
    /** Etiqueta del bloque (ej: 'Origen', 'Destino') */
    @Input() label: string = 'Ubicación';

    savedLocations: any[] = [];
    selectedSavedId: number | null = null;

    searchQuery: string = '';
    searchResults: any[] = [];
    showResults: boolean = false;
    searchLoading: boolean = false;

    private searchSubject = new Subject<string>();

    // Estado de guardado
    showSaveForm: boolean = false;
    saveLabel: string = '';
    saving: boolean = false;

    constructor(private http: HttpClient, private generalService: GeneralService) { }

    ngOnInit(): void {
        this.loadSavedLocations();

        // Búsqueda Nominatim con debounce
        this.searchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            switchMap(query => {
                if (query.length < 3) { this.searchResults = []; return of([]); }
                this.searchLoading = true;
                return this.http.get<any[]>(
                    `${NOMINATIM}?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=mx&limit=5`,
                    { headers: { 'User-Agent': USER_AGENT } }
                );
            })
        ).subscribe({
            next: (results: any[]) => {
                this.searchLoading = false;
                this.searchResults = results ?? [];
                this.showResults = this.searchResults.length > 0;
            },
            error: () => { this.searchLoading = false; }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['clientId'] && this.clientId) {
            this.loadSavedLocations();
        }
    }

    // ─── Ubicaciones guardadas ─────────────────────────────────────────────────

    loadSavedLocations(): void {
        if (!this.clientId) return;
        const headers = new HttpHeaders().set('X-Api-Key', API_KEY);
        this.http.post<any>(`${END_POINT}/savedlocation/getlocations`, { client_id: this.clientId }, { headers })
            .subscribe(res => {
                if (!res.error) this.savedLocations = res.message;
            });
    }

    applySavedLocation(): void {
        if (!this.selectedSavedId) return;
        const loc = this.savedLocations.find(l => l.id == this.selectedSavedId);
        if (!loc) return;
        this.group.patchValue({
            street: loc.street ?? '',
            numberExt: loc.number_ext ?? '',
            numberInt: loc.number_int ?? '',
            cp: loc.postal_code ?? '',
            locality: loc.locality ?? '',
            city: loc.city ?? '',
        });
    }

    clearSaved(): void {
        this.selectedSavedId = null;
    }

    deleteSelectedLocation(event: Event): void {
        const loc = this.savedLocations.find(l => l.id == this.selectedSavedId);
        if (loc) this.deleteLocation(loc, event);
    }

    deleteLocation(loc: any, event: Event): void {
        event.stopPropagation();
        if (!confirm(`¿Eliminar "${loc.label}"?`)) return;
        const headers = new HttpHeaders().set('X-Api-Key', API_KEY);
        this.http.post<any>(`${END_POINT}/savedlocation/delete`, { id: loc.id }, { headers })
            .subscribe(res => {
                if (res.error) {
                    this.generalService.showMessage('warn', res.message);
                } else {
                    this.savedLocations = this.savedLocations.filter(l => l.id !== loc.id);
                    if (this.selectedSavedId == loc.id) this.selectedSavedId = null;
                }
            });
    }

    // ─── Búsqueda Nominatim ────────────────────────────────────────────────────

    onSearchInput(): void {
        this.searchSubject.next(this.searchQuery);
    }

    selectResult(result: any): void {
        const addr = result.address ?? {};
        // Intentar mapear la ciudad de Nominatim contra la lista de ciudades del sistema
        const cityName = addr.city || addr.state_district || addr.county || '';
        const cityMatch = this.cityList.find(c =>
            c.value?.toLowerCase().includes(cityName.toLowerCase()) ||
            cityName.toLowerCase().includes(c.value?.toLowerCase())
        );

        this.group.patchValue({
            street: addr.road ?? '',
            numberExt: addr.house_number ?? '',
            cp: addr.postcode ?? '',
            locality: addr.suburb || addr.neighbourhood || addr.village || '',
            city: cityMatch ? cityMatch.id : '',
        });

        this.searchQuery = result.display_name;
        this.showResults = false;
        this.searchResults = [];
    }

    hideResults(): void {
        setTimeout(() => { this.showResults = false; }, 200);
    }

    // ─── Guardar ubicación ─────────────────────────────────────────────────────

    toggleSaveForm(): void {
        this.showSaveForm = !this.showSaveForm;
        if (!this.showSaveForm) this.saveLabel = '';
    }

    saveLocation(): void {
        if (!this.saveLabel.trim()) {
            this.generalService.showMessage('warn', 'Ingresa un nombre para la ubicación.');
            return;
        }
        if (!this.clientId) {
            this.generalService.showMessage('warn', 'Solo los clientes pueden guardar ubicaciones.');
            return;
        }
        this.saving = true;
        const val = this.group.value;
        const headers = new HttpHeaders().set('X-Api-Key', API_KEY);
        const payload = {
            client_id: this.clientId,
            label: this.saveLabel.trim(),
            street: val.street ?? null,
            number_ext: val.numberExt ?? null,
            number_int: val.numberInt ?? null,
            postal_code: val.cp ?? null,
            locality: val.locality ?? null,
            city: val.city ?? null,
        };
        this.http.post<any>(`${END_POINT}/savedlocation/save`, payload, { headers })
            .subscribe({
                next: res => {
                    this.saving = false;
                    if (res.error) {
                        this.generalService.showMessage('warn', res.message);
                    } else {
                        this.generalService.showMessage('success', '¡Ubicación guardada!');
                        this.showSaveForm = false;
                        this.saveLabel = '';
                        this.loadSavedLocations();
                    }
                },
                error: () => {
                    this.saving = false;
                    this.generalService.showMessage('warn', 'Error al guardar la ubicación.');
                }
            });
    }
}
